// The shared settings frame (21-user-settings.md, "The settings window"; 18-channel-settings.md;
// 02-building-blocks.md, "Full-window settings"): a full-window page over the app (the menu on the
// rail's colour, the open page on its own surface; one at a time on a phone), and the rows every
// settings page is made of: groups of cards with a title, a line and a control (a choice, a
// switch, a button), shared by the user's, a channel's and the company's settings.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, IconButton } from '../blocks/Button';
import { Dialog } from '../blocks/Dialog';
import { Icon, icons } from '../blocks/icons';
import { Menu } from '../blocks/Menu';
import { useScreen } from '../blocks/Page';
import { Spinner } from '../blocks/Wait';
import { setting, useSettings, writeSetting, type Settings } from '../core/settings';
import { t } from '../i18n';
import { navigate, useAddress } from '../core/router';
import './settings.css';

export interface Entry {
  key: string;
  label: string;
  icon: string;
  /** The icon while the page is open. */
  iconOpen?: string;
}
export interface Group {
  heading?: string;
  entries: Entry[];
}

export interface WindowProps {
  label: string;
  /** Above the menu: the room's picture and name, or the person's card. */
  top?: ReactNode;
  groups: Group[];
  /** Under the menu (Sign out, the version). */
  foot?: ReactNode;
  page?: string;
  onPage: (key: string | undefined) => void;
  onClose: () => void;
  render: (page: string) => ReactNode;
  /** A page's title bar in place of its name (My account's profile card); the name is still read out. */
  titleOf?: (page: string) => ReactNode;
}

let behind = false;
/** The page behind the window changes and the window stays (a room deleted from under it, 18). */
export const goBehind = (to: string) => ((behind = true), navigate(to, { replace: true }));

export function SettingsWindow({ label, top, groups, foot, page, onPage, onClose, render, titleOf }: WindowProps) {
  const phone = useScreen() === 'phone';
  const showMenu = !phone || !page;
  const showPage = !!page && (!phone || page);
  const title = groups.flatMap((g) => g.entries).find((e) => e.key === page)?.label ?? '';
  const menu = useRef<HTMLElement>(null);
  const [closing, setClosing] = useState(false);
  // Going somewhere else (a person card's "Message", a link) closes the window: it has no address of its own.
  const address = useAddress();
  const opened = useRef(address);
  useEffect(() => {
    if (address === opened.current) return;
    if (behind) return void ((behind = false), (opened.current = address));
    onClose();
  }, [address]); // eslint-disable-line react-hooks/exhaustive-deps
  // Focus starts on the open page's entry, else the menu's first button.
  useEffect(() => (menu.current?.querySelector<HTMLElement>('[aria-pressed="true"]') ?? menu.current?.querySelector<HTMLElement>('button'))?.focus(), []);
  /** Escape typed in a field doesn't close it (02); closing fades and zooms out in 200 ms (01, Motion). */
  const close = () => {
    if (document.activeElement?.matches('input:not([type=checkbox], [type=range], [type=color]), textarea, [contenteditable="true"]')) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return onClose();
    setClosing(true);
    setTimeout(onClose, 200);
  };
  return (
    <Dialog label={label} onClose={close} className={`settings-window${phone ? ' phone' : ''}${closing ? ' closing' : ''}`}>
      {showMenu && (
        <nav className="settings-menu" aria-label={label} ref={menu}>
          {phone && <IconButton className="settings-close" label={t('common.close')} icon={icons.close} onClick={close} />}
          {top}
          {groups.map((g, n) => (
            <div key={n} className="settings-group">
              {g.heading && <h3>{g.heading}</h3>}
              {g.entries.map((e) => (
                <button key={e.key} type="button" className="settings-entry" aria-pressed={page === e.key} data-desyr-settings-entry={e.key} onClick={() => onPage(e.key)}>
                  <Icon svg={page === e.key ? (e.iconOpen ?? e.icon) : e.icon} size={20} />
                  {e.label}
                </button>
              ))}
            </div>
          ))}
          {foot}
        </nav>
      )}
      {showPage && (
        <section className="settings-page" data-desyr-settings-page={page}>
          <header className="settings-title">
            {titleOf?.(page!) ?? <h2>{title}</h2>}
            <span className="settings-x">
              <IconButton label={t(phone ? 'settings.backToMenu' : 'common.close')} icon={icons.close} onClick={() => (phone ? onPage(undefined) : close())} />
              {!phone && <kbd aria-hidden="true">ESC</kbd>}
            </span>
          </header>
          <div className="settings-content">{render(page!)}</div>
        </section>
      )}
    </Dialog>
  );
}

export const Section = ({ title, children }: { title?: string; children: ReactNode }) => (
  <section className="settings-section">
    {title && <h3>{title}</h3>}
    <div className="settings-cards">{children}</div>
  </section>
);

/** One setting: an icon, a title, a line, the control at the end, and sometimes more under it. */
export function Card({ icon, title, text, control, error, hook, children }: { icon?: string; title: ReactNode; text?: ReactNode; control?: ReactNode; error?: string; hook?: string; children?: ReactNode }) {
  return (
    <div className="settings-card" data-desyr-setting={hook}>
      <div className="settings-card-row">
        {icon && (
          <span className="settings-icon">
            <Icon svg={icon} size={20} />
          </span>
        )}
        <div className="settings-card-text">
          <div className="settings-card-title">{title}</div>
          {text && <div className="settings-card-line">{text}</div>}
        </div>
        {control}
      </div>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
      {children}
    </div>
  );
}

/** A choice button: the current choice, opening a menu of choices (the current one marked). */
export function Choice<T extends string | number>({ label, value, options, disabled, busy, onChange }: { label: string; value: T; options: { value: T; label: string; lang?: string }[]; disabled?: boolean; busy?: boolean; onChange: (v: T) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <>
      <Button ref={ref} small disabled={disabled || busy} aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(true)}>
        {busy && <Spinner size={14} />}
        {options.find((o) => o.value === value)?.label ?? value}
      </Button>
      {open && (
        <Menu label={label} anchor={ref.current} onClose={() => setOpen(false)}>
          {options.map((o) => (
            <button
              key={String(o.value)}
              type="button"
              role="menuitemradio"
              className="menu-item"
              aria-checked={o.value === value}
              onClick={() => {
                setOpen(false);
                if (o.value !== value) onChange(o.value);
              }}
            >
              <span className="menu-label" lang={o.lang}>{o.label}</span>
              {o.value === value && <Icon svg={icons.check} size={16} />}
            </button>
          ))}
        </Menu>
      )}
    </>
  );
}

/** A row of a settings table: a device setting with its switch (inverted: on means the field is off). */
export interface SwitchRow {
  field: keyof Settings;
  icon: string;
  title: string;
  text?: string;
  invert?: boolean;
  /** The value while the field is absent, where it isn't the settings' default (24-hour time: by language). */
  fallback?: () => boolean;
}

/** Switch cards from a table; each change is kept at once (21, "Keeping the settings"). */
export function Switches({ rows }: { rows: SwitchRow[] }) {
  useSettings();
  return rows.map((r) => <SwitchCard key={r.field} {...r} />);
}

function SwitchCard({ field, icon, title, text, invert, fallback }: SwitchRow) {
  const id = useId();
  const on = !!(setting(field) ?? fallback?.()) !== !!invert;
  return (
    <Card
      icon={icon}
      hook={field}
      title={<span id={id}>{title}</span>}
      text={text}
      control={<button type="button" role="switch" className="switch" aria-checked={on} aria-labelledby={id} onClick={() => writeSetting(field, (!on !== !!invert) as never)} />}
    />
  );
}
